import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthContext';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  Tabs,
  Tab,
  Autocomplete,
  Chip,
  InputAdornment,
  IconButton,
  Stack,
  Divider,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  PersonAdd,
  Group,
  Upload,
  Cancel,
  Save,
  Download,
  Email,
  CheckCircle,
  AccessTime,
  HourglassEmpty,
  Search as SearchIcon,
  Clear as ClearIcon,
  DateRange as DateRangeIcon,
  CalendarToday as CalendarIcon,
  Refresh,
  Campaign,
} from '@mui/icons-material';
import Papa from 'papaparse';
import { useSnackbar } from 'notistack';
import { adminService, clearAdminCache } from '../../../services/adminService';
import mailchimpService from '../../../services/mailChimpService';
import UserTable from './UserTable'; 
import BulkUserUpload from './BulkUserUpload';
import CSVProcessingDialog from './CSVProcessingDialog';
import AnnouncementEmailDialog from './AnnouncementEmailDialog';

const DEPARTMENTS = [
  'Operations',
  'Engagement',
  'Strategy',
  'Finance',
  'Other'
];

const ROLES = [
  { 
    value: 'admin', 
    label: 'Admin', 
    description: 'System administrator with full access'
  },
  { 
    value: 'updated_write', 
    label: 'Updated Write', 
    description: 'Can modify updated projection data' 
  },
  { 
    value: 'updated_read', 
    label: 'Updated Read', 
    description: 'Can view updated projection data' 
  },
  { 
    value: 'summary_updated', 
    label: 'Summary Updated', 
    description: 'Can view summary pages with updated projections' 
  },
  { 
    value: 'summary_public', 
    label: 'Summary Public', 
    description: 'Can view summary pages with public projections only' 
  },
  { 
    value: 'public', 
    label: 'Public Access', 
    description: 'Can view public projection data only' 
  }
];

// Which top-level tool(s) a user can open. Independent of `role`, which
// governs capability *within* a product. Mirrors PRODUCT_* in
// backend/app/utils/auth_helpers.py.
const PRODUCTS = [
  { value: 'school_explorer', label: 'School Explorer' },
  { value: 'city_explorer', label: 'City Explorer' },
];

// Keep role checks simple and aligned with auth_helpers.py
export const isAdmin = (role) => role === 'admin';
export const canWriteProjections = (role) => ['admin', 'updated_write'].includes(role);
export const canReadProjections = (role) => ['admin', 'updated_write', 'updated_read'].includes(role);

// Compact StatsCard Component
const StatsCard = ({ title, value, icon: Icon, color = 'primary.main' }) => (
  <Paper 
    elevation={1} 
    sx={{ 
      p: 1.5, 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1.5,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: 2
      }
    }}
  >
    <Box
      sx={{
        p: 1,
        backgroundColor: color === 'primary.main' ? 'primary.light' : `${color.split('.')[0]}.light`,
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon sx={{ fontSize: 20, color }} />
    </Box>
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h6" sx={{ lineHeight: 1, mb: 0.5 }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
        {title}
      </Typography>
    </Box>
  </Paper>
);

const UserManagementPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mode, setMode] = useState('list');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersEnriched, setUsersEnriched] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [availableTeamGroups, setAvailableTeamGroups] = useState([]);
  const [batchSending, setBatchSending] = useState(false);
  const [exportingLinks, setExportingLinks] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [usersToDelete, setUsersToDelete] = useState([]);
  const [bulkDeleteInProgress, setBulkDeleteInProgress] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [csvProcessing, setCsvProcessing] = useState(false);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [funderFilter, setFunderFilter] = useState('');
  const [teamAssignmentFilter, setTeamAssignmentFilter] = useState('all'); // all, no_teams, no_team_groups, no_teams_or_groups, has_teams
  const [roleFilter, setRoleFilter] = useState('');
  const [invitingUser, setInvitingUser] = useState(null);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Last login filter state
  const [lastLoginFilter, setLastLoginFilter] = useState('all'); // all, never, recent, old
  const [loginDateRange, setLoginDateRange] = useState({
    start: null,
    end: null
  });
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [cityExplorerLocationsLoading, setCityExplorerLocationsLoading] = useState(false);
  const [availableCityExplorerLocations, setAvailableCityExplorerLocations] = useState([]);
  const [progressDialog, setProgressDialog] = useState({
    open: false,
    processed: 0,
    total: 0
  });
  
   // Form data with teams
   const [formData, setFormData] = useState({
    email: '',
    firstName: '', 
    lastName: '', 
    department: '',
    role: '',
    products: ['school_explorer'],
    teams: [],
    teamGroups: [],
    cityExplorerLocations: [],
    funder: ''    
  });

  const loadingRef = useRef(false);
  
  const isMounted = useRef(true);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Memoize fetch functions
  const fetchTeams = useCallback(async () => {
    if (!isMounted.current) return;
    
    try {
      // Show loading indicator
      setTeamsLoading(true);
      
      // Request all teams for admin users with standardized parameters
      const teamsData = await adminService.listTeams(0, 1000, enqueueSnackbar, true);
      
      if (isMounted.current) {
        // Sort the teams alphabetically by name
        // Always expect response.teams format for consistency
        let sortedTeams = [];
        
        if (teamsData && typeof teamsData === 'object' && Array.isArray(teamsData.teams)) {
          sortedTeams = [...teamsData.teams].sort((a, b) => 
            (a.name || '').localeCompare(b.name || '', undefined, {sensitivity: 'base'})
          );
        }
        
        setAvailableTeams(sortedTeams);
      }
    } catch (err) {
      if (isMounted.current) {
        console.error('Error fetching teams:', err);
        enqueueSnackbar(err.message || 'Failed to fetch teams', { variant: 'error' });
        setAvailableTeams([]); // Set empty array on error
      }
    } finally {
      // Hide loading indicator when done
      if (isMounted.current) {
        setTeamsLoading(false);
      }
    }
  }, [enqueueSnackbar]);
  
  const fetchTeamGroups = useCallback(async () => {
    if (!isMounted.current) return;
    
    try {
      const groupsData = await adminService.listTeamGroups();
      if (isMounted.current) {
        setAvailableTeamGroups(groupsData);
      }
    } catch (err) {
      if (isMounted.current) {
        console.error('Error fetching team groups:', err);
        enqueueSnackbar(err.message || 'Failed to fetch team groups', { variant: 'error' });
      }
    }
  }, [enqueueSnackbar]);

  const fetchCityExplorerLocations = useCallback(async () => {
    if (!isMounted.current) return;

    try {
      setCityExplorerLocationsLoading(true);
      const data = await adminService.listCityExplorerLocations(enqueueSnackbar);
      if (isMounted.current) {
        const sorted = Array.isArray(data?.locations)
          ? [...data.locations].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
          : [];
        setAvailableCityExplorerLocations(sorted);
      }
    } catch (err) {
      if (isMounted.current) {
        console.error('Error fetching City Explorer locations:', err);
        setAvailableCityExplorerLocations([]);
      }
    } finally {
      if (isMounted.current) {
        setCityExplorerLocationsLoading(false);
      }
    }
  }, [enqueueSnackbar]);

  const fetchUsers = useCallback(async () => {
    if (!isMounted.current) return;
    try {
      setLoading(true);
      setUsersEnriched(false);
      const minimalResponse = await adminService.listUsersMinimal(enqueueSnackbar);
      if (!isMounted.current) return;
      const minimalList = minimalResponse.users || (Array.isArray(minimalResponse) ? minimalResponse : []);
      setUsers(minimalList);
      setTotalUsers(minimalResponse.pagination?.total ?? minimalList.length);
      setLoading(false);

      adminService.listUsersFull(enqueueSnackbar).then((fullResponse) => {
        if (!isMounted.current) return;
        const fullList = fullResponse.users || [];
        const fullByUid = Object.fromEntries((fullList).map((u) => [u.uid, u]));
        setUsers((prev) =>
          prev.map((u) => (fullByUid[u.uid] ? { ...fullByUid[u.uid] } : u))
        );
        setUsersEnriched(true);
      }).catch((err) => {
        if (isMounted.current) {
          console.error('Error enriching users:', err);
          enqueueSnackbar(err.message || 'Details are still loading', { variant: 'warning' });
        }
      });
    } catch (err) {
      if (isMounted.current) {
        console.error('Error fetching users:', err);
        enqueueSnackbar(err.message || 'Failed to fetch users', { variant: 'error' });
      }
      if (isMounted.current) setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Sorting logic
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Helper function to get sortable value from user
  const getSortValue = useCallback((user, key) => {
    switch (key) {
      case 'name':
        return (user.firstName && user.lastName) 
          ? `${user.firstName} ${user.lastName}`.toLowerCase()
          : (user.name || user.email || '').toLowerCase();
      case 'funder':
        return (user.funder || '').toLowerCase();
      case 'lastActivity':
        return user.lastSignInTime ? new Date(user.lastSignInTime).getTime() : 0;
      case 'status':
        return user.disabled ? 'suspended' : 'active';
      default:
        return '';
    }
  }, []);

  // Helper function to filter by last login
  const filterByLastLogin = useCallback((user) => {
    const lastLogin = user.lastSignInTime ? new Date(user.lastSignInTime) : null;
    
    // If custom date range is active, use that
    if (lastLoginFilter === 'dateRange' && (loginDateRange.start || loginDateRange.end)) {
      if (!lastLogin) return false; // No login date, can't be in range
      
      // Convert last login to YYYY-MM-DD format for comparison
      const loginDateStr = lastLogin.getFullYear() + '-' + 
                           String(lastLogin.getMonth() + 1).padStart(2, '0') + '-' + 
                           String(lastLogin.getDate()).padStart(2, '0');
      
      // Handle start date (inclusive)
      if (loginDateRange.start && loginDateStr < loginDateRange.start) {
        return false;
      }
      
      // Handle end date (inclusive)
      if (loginDateRange.end && loginDateStr > loginDateRange.end) {
        return false;
      }
      
      
      return true;
    }
    
    // Otherwise use preset filters
    if (lastLoginFilter === 'all') return true;
    
    const now = new Date();
    
    switch (lastLoginFilter) {
      case 'never':
        return !lastLogin;
      case 'recent': // Last 30 days
        if (!lastLogin) return false;
        const daysDiff = (now - lastLogin) / (1000 * 60 * 60 * 24);
        return daysDiff <= 30;
      case 'old': // More than 90 days ago
        if (!lastLogin) return true; // Never logged in counts as "old"
        const daysDiffOld = (now - lastLogin) / (1000 * 60 * 60 * 24);
        return daysDiffOld > 90;
      default:
        return true;
    }
  }, [lastLoginFilter, loginDateRange]);

  // Helper function to filter by team assignment
  const filterByTeamAssignment = useCallback((user) => {
    const hasTeams = user.teams && user.teams.length > 0;
    const hasTeamGroups = user.teamGroups && user.teamGroups.length > 0;
    
    switch (teamAssignmentFilter) {
      case 'no_teams':
        return !hasTeams;
      case 'no_team_groups':
        return !hasTeamGroups;
      case 'no_teams_or_groups':
        return !hasTeams && !hasTeamGroups;
      case 'has_teams':
        return hasTeams || hasTeamGroups;
      case 'all':
      default:
        return true;
    }
  }, [teamAssignmentFilter]);

  // Helper function to filter by role
  const filterByRole = useCallback((user) => {
    if (!roleFilter) return true;
    return user.role === roleFilter;
  }, [roleFilter]);

  // Helper function to get user-friendly role label
  const getRoleLabel = useCallback((roleValue) => {
    const role = ROLES.find(r => r.value === roleValue);
    return role ? role.label : roleValue;
  }, []);

  // Sort users based on current sort configuration
  const sortedUsers = useMemo(() => {
    const filtered = users.filter(user => 
      filterByLastLogin(user) && filterByTeamAssignment(user) && filterByRole(user)
    );
    
    if (!sortConfig.key) return filtered;
    
    return [...filtered].sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, sortConfig, getSortValue, filterByLastLogin, filterByTeamAssignment, filterByRole]);

  // Helper functions for date range
  const clearDateRange = useCallback(() => {
    setLoginDateRange({ start: null, end: null });
    setLastLoginFilter('all');
    setShowDateRangePicker(false);
  }, []);

  const applyDateRange = useCallback(() => {
    if (loginDateRange.start || loginDateRange.end) {
      setLastLoginFilter('dateRange');
    }
    setShowDateRangePicker(false);
  }, [loginDateRange]);

  const getDateRangeLabel = useCallback(() => {
    if (lastLoginFilter !== 'dateRange' || (!loginDateRange.start && !loginDateRange.end)) {
      return 'All Activity';
    }
    
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      // dateStr is already in YYYY-MM-DD format, convert to local display format
      const d = new Date(dateStr + 'T00:00:00'); // Add time to avoid timezone issues
      return d.toLocaleDateString();
    };
    
    const start = formatDate(loginDateRange.start);
    const end = formatDate(loginDateRange.end);
    
    if (start && end) {
      return `${start} - ${end}`;
    } else if (start) {
      return `From ${start}`;
    } else if (end) {
      return `Until ${end}`;
    }
    return 'Date Range';
  }, [lastLoginFilter, loginDateRange]);

  useEffect(() => {
    if (!user?.role === 'admin') {
      navigate('/dashboard');
      return;
    }
  }, [user?.role, navigate]);

  // Data fetching effect
  useEffect(() => {
    if (!user?.role === 'admin') {
      navigate('/dashboard');
      return;
    }
  
    isMounted.current = true;
    if (loadingRef.current) return;
    
    const loadData = async () => {
      try {
        loadingRef.current = true;
        await Promise.all([
          fetchUsers(), // This will now use the page and pageSize state
          fetchTeams(),
          fetchTeamGroups(),
          fetchCityExplorerLocations()
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
        if (isMounted.current) {
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      } finally {
        if (isMounted.current) {
          loadingRef.current = false;
        }
      }
    };
  
    loadData();
  
    return () => {
      isMounted.current = false;
      loadingRef.current = false;
    };
  }, [user?.role, fetchUsers, fetchTeams, fetchTeamGroups, fetchCityExplorerLocations, enqueueSnackbar, navigate]);


  const handleTestEmail = useCallback(async () => {
    if (!isMounted.current) return;
    
    try {
      setTestEmailLoading(true);
      // Replace with mailchimpService
      await mailchimpService.sendTestEmail();
      if (isMounted.current) {
        enqueueSnackbar('Test email sent successfully via Mailchimp', { variant: 'success' });
      }
    } catch (error) {
      if (isMounted.current) {
        console.error('Test email error:', error);
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    } finally {
      if (isMounted.current) {
        setTestEmailLoading(false);
      }
    }
  }, [enqueueSnackbar]);

  const handleBatchInvite = async (userIds) => {
    try {
      setBatchSending(true);
      setProgressDialog({
        open: true,
        processed: 0,
        total: userIds.length
      });
      
      // Use mailchimpService to send batch invites
      const response = await mailchimpService.sendBulkEmails(userIds, enqueueSnackbar);
      
      enqueueSnackbar(
        `Successfully sent ${response.sent_count || 0} invitations`,
        { variant: 'success' }
      );
      
      // Refresh user list to update "lastInviteSent" status
      await fetchUsers();
    } catch (error) {
      console.error('Batch invite error:', error);
      enqueueSnackbar(
        `Failed to send batch invites: ${error.message || 'Unknown error'}`,
        { variant: 'error' }
      );
    } finally {
      setBatchSending(false);
      setProgressDialog({ open: false, processed: 0, total: 0 });
    }
  };

  // In UserManagementPanel.js - improved bulk delete function
  const handleBulkDelete = async (userIds) => {
    try {
       // Close the confirmation dialog first
      setBulkDeleteDialogOpen(false);
      
      // Then set the in-progress state and show the progress dialog
      setBulkDeleteInProgress(true);
      setProgressDialog({
        open: true,
        processed: 0,
        total: userIds.length
      });
      
      // Process each user deletion sequentially
      let successCount = 0;
      let failedCount = 0;
      const failedUsers = [];

      for (let i = 0; i < userIds.length; i++) {
        try {
          const userId = userIds[i];
          // Find the user email for the deletion process
          const userToDelete = users.find(u => u.uid === userId);
          
          if (userToDelete) {
            // Set the specific user as being deleted (for UI feedback)
            setDeletingUser(userId);
            
            // Call the API and wait for completion
            await adminService.deleteUser(userId, userToDelete.email);
            successCount++;
          }
          
          // Update progress after operation completes
          setProgressDialog(prev => ({
            ...prev,
            processed: i + 1
          }));
        } catch (error) {
          console.error(`Failed to delete user ${userIds[i]}:`, error);
          failedCount++;
          failedUsers.push({
            uid: userIds[i],
            error: error.message
          });
        } finally {
          // Clear the deleting state for this specific user
          setDeletingUser(null);
        }
      }
      
      // Only refresh the user list after all operations are complete
      await fetchUsers();
      
      // Show appropriate message based on results
      if (failedCount === 0) {
        enqueueSnackbar(
          `Successfully deleted ${successCount} users`,
          { variant: 'success' }
        );
      } else {
        enqueueSnackbar(
          `Deleted ${successCount} users with ${failedCount} failures`,
          { variant: 'warning' }
        );
      }
      
      // Close dialog and reset state
      setBulkDeleteDialogOpen(false);
      setUsersToDelete([]);
    } catch (error) {
      console.error('Bulk delete error:', error);
      enqueueSnackbar(
        `Failed to process bulk delete: ${error.message || 'Unknown error'}`,
        { variant: 'error' }
      );
    } finally {
      setBulkDeleteInProgress(false);
      setProgressDialog({ open: false, processed: 0, total: 0 });
      setDeletingUser(null); // Ensure deleting state is cleared
    }
  };

  const handleExportActivationLinks = async (userIds) => {
    try {
      setExportingLinks(true);
      const response = await adminService.generateActivationLinks(userIds, enqueueSnackbar);

      if (!response || !response.generated || response.generated.length === 0) {
        enqueueSnackbar('No activation links were generated', { variant: 'warning' });
        return;
      }

      const rows = response.generated.map(item => ({
        Name: item.name || '',
        Email: item.email,
        Teams: (item.teams || []).join('; '),
        'Team Groups': (item.teamGroups || []).join('; '),
        'Activation Link': item.activationUrl,
        'Expires': item.expiresAt ? new Date(item.expiresAt).toLocaleDateString() : ''
      }));

      const csv = Papa.unparse(rows);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activation_links_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const errorCount = response.errors?.length || 0;
      if (errorCount > 0) {
        enqueueSnackbar(
          `Exported ${response.generated.length} links (${errorCount} failed)`,
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar(
          `Exported ${response.generated.length} activation link${response.generated.length !== 1 ? 's' : ''}`,
          { variant: 'success' }
        );
      }
    } catch (error) {
      console.error('Export activation links error:', error);
      enqueueSnackbar(
        `Failed to export activation links: ${error.message || 'Unknown error'}`,
        { variant: 'error' }
      );
    } finally {
      setExportingLinks(false);
    }
  };

  // Bulk Delete Dialog Component - Defined as a nested component within UserManagementPanel
  const BulkDeleteDialog = () => (
    <Dialog 
      open={bulkDeleteDialogOpen} 
      onClose={() => {
        if (!bulkDeleteInProgress) {
          setBulkDeleteDialogOpen(false);
          setUsersToDelete([]);
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Confirm Bulk User Deletion</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          Are you sure you want to delete {usersToDelete.length} selected users?
        </Typography>
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          Warning: This action cannot be undone. All selected user accounts will be permanently removed.
        </Typography>
        
        {/* Show warning if admin is deleting their own account */}
        {usersToDelete.includes(user?.uid) && (
          <Typography color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
            Warning: You are about to delete your own account. You will be logged out immediately.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => {
            setBulkDeleteDialogOpen(false);
            setUsersToDelete([]);
          }}
          disabled={bulkDeleteInProgress}
        >
          Cancel
        </Button>
        <Button 
          color="error" 
          onClick={() => handleBulkDelete(usersToDelete)}
          disabled={bulkDeleteInProgress}
          startIcon={bulkDeleteInProgress ? <CircularProgress size={20} /> : null}
        >
          {bulkDeleteInProgress ? 'Processing...' : 'Delete Selected Users'}
        </Button>
      </DialogActions>
    </Dialog>
  );


  // Render user list using UserTable component
  const renderUserList = () => (
    <Box sx={{ mt: 2 }}>
      <UserTable
        users={sortedUsers}
        usersEnriched={usersEnriched}
        availableTeams={availableTeams}
        onSort={handleSort}
        sortConfig={sortConfig}
        onSendInvite={async (user) => {
          try {
            setInvitingUser(user.uid);
            await mailchimpService.sendUserInvite(user.uid);
            enqueueSnackbar('Invitation sent successfully via Mailchimp', { variant: 'success' });
            await fetchUsers();
          } catch (error) {
            enqueueSnackbar(error.message, { variant: 'error' });
          } finally {
            setInvitingUser(null);
          }
        }}
        invitingUser={invitingUser}
        onBatchInvite={handleBatchInvite}
        batchSending={batchSending}
        onExportActivationLinks={handleExportActivationLinks}
        exportingLinks={exportingLinks}
        deletingUser={deletingUser}
        onBulkDelete={(selectedUserIds) => {
          setUsersToDelete(selectedUserIds);
          setBulkDeleteDialogOpen(true);
        }}
        bulkDeleteInProgress={bulkDeleteInProgress}
        onEdit={(user) => {
          setSelectedUser(user);
          setFormData({
            email: user.email,
            firstName: user.firstName, 
            lastName: user.lastName,  
            department: user.department,
            role: user.role,
            products: user.products?.length ? user.products : ['school_explorer'],
            teams: user.teams?.map(team => team.id) || [],
            teamGroups: user.teamGroups?.map(group => group.id) || [],
            cityExplorerLocations: user.cityExplorerLocations || [],
            funder: user.funder || ''
          });
          setMode('edit');
        }}
        onSuspend={async (user) => {
          try {
            if (user.disabled) {
              await adminService.reactivateUser(user.uid);
            } else {
              await adminService.suspendUser(user.uid);
            }
            await fetchUsers();
            enqueueSnackbar(
              `User ${user.disabled ? 'reactivated' : 'suspended'} successfully`, 
              { variant: 'success' }
            );
          } catch (error) {
            enqueueSnackbar(error.message, { variant: 'error' });
          }
        }}
        onDelete={(user) => {
          setUserToDelete(user);
          setDeleteDialogOpen(true);
        }}
        loading={loading}
        // External filter props
        externalSearchTerm={searchTerm}
        externalSelectedStatus={selectedStatus}
        externalFunderFilter={funderFilter}
        hideControls={true}
        getRoleLabel={getRoleLabel}
      />

      {/* User count info */}
      {users.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {users.length} users loaded
          </Typography>
        </Box>
      )}
    </Box>
  );

  // Modified form rendering
  const renderForm = () => (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="user management tabs">
          <Tab label="User Details" />
          <Tab label="Assign Teams" />
          <Tab label="Assign Team Groups" />
          <Tab label="City Explorer Locations" />
        </Tabs>
      </Box>
      
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        {/* Tab 1: User Details */}
        {activeTab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Tooltip title="Enter the user's authentication email address" placement="top">
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  required
                  disabled={mode === 'edit'}
                  helperText={mode === 'edit' ? "Email changes require administrator approval" : "User's primary login email"}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Tooltip title="Enter the user's first name as it will appear in the system" placement="top">
                <TextField
                  fullWidth
                  label=" First Name"
                  value={formData.firstName}
                  onChange={e => setFormData({...formData, firstName: e.target.value})}
                  required
                  helperText="User's first name"
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Tooltip title="Enter the user's last name as it will appear in the system" placement="top">
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={e => setFormData({...formData, lastName: e.target.value})}
                  required
                  helperText="User's last name"
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Tooltip title="Select the user's access level in the system" placement="top">
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={e => setFormData({...formData, role: e.target.value})}
                    label="Role"
                    required
                  >
                    {ROLES.map(role => (
                      <MenuItem key={role.value} value={role.value}>
                        <Tooltip title={role.description} placement="right">
                          <span>{role.label}</span>
                        </Tooltip>
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Determines user's system access level and permissions
                  </FormHelperText>
                </FormControl>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Tooltip title="Select the user's department from the available options" placement="top">
                <FormControl fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={formData.department}
                    onChange={e => setFormData({...formData, department: e.target.value})}
                    label="Department"
                    required
                  >
                    {DEPARTMENTS.map(dept => (
                      <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    User's assigned department within the organization
                  </FormHelperText>
                </FormControl>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Tooltip title="Which top-level tool(s) this user can open. Admins always have access to everything regardless of this setting." placement="top">
                <FormControl fullWidth>
                  <InputLabel>Products</InputLabel>
                  <Select
                    multiple
                    value={formData.products || []}
                    onChange={e => {
                      const { value } = e.target;
                      setFormData({
                        ...formData,
                        products: typeof value === 'string' ? value.split(',') : value
                      });
                    }}
                    label="Products"
                    renderValue={(selected) => selected
                      .map(value => PRODUCTS.find(p => p.value === value)?.label || value)
                      .join(', ')}
                  >
                    {PRODUCTS.map(product => (
                      <MenuItem key={product.value} value={product.value}>
                        <Checkbox checked={(formData.products || []).includes(product.value)} />
                        <ListItemText primary={product.label} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Which product(s) this user can access. Users with both will pick one after logging in.
                  </FormHelperText>
                </FormControl>
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Tooltip title="Enter the funder for the users' access (optional)" placement="top">
                <TextField
                  fullWidth
                  label="Funder"
                  value={formData.funder || ''}  // Handle null/undefined values
                  onChange={e => setFormData({...formData, funder: e.target.value || null})}
                  helperText="Associated funding organization (if applicable)"
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setMode('list');
                  resetForm();
                }}
                startIcon={<Cancel />}
              >
                Cancel
              </Button>
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={<Save />}
                  sx={{ mr: 2 }}
                >
                  {loading ? 'Saving...' : (mode === 'edit' ? 'Save Changes' : 'Create User')}
                </Button>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={() => setActiveTab(1)}
                >
                  Next: Assign Teams
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
        
        {/* Tab 2: Assign Teams */}
        {activeTab === 1 && (
          <>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Assign Teams
            </Typography>
            
            {/* Multi-select with Chips implementation */}
            <Autocomplete
              multiple
              id="teams-autocomplete"
              options={Array.isArray(availableTeams) ? availableTeams : []}
              getOptionLabel={(option) => option.name}
              loading={teamsLoading}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography>{option.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      ({option.school_count || 0} schools)
                    </Typography>
                  </Box>
                </li>
              )}
              value={(Array.isArray(availableTeams) ? availableTeams : []).filter(team => 
                formData.teams.includes(team.id)
              )}
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  teams: newValue.map(team => team.id)
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Search and select teams"
                  placeholder="Type to search teams"
                  helperText="Selected teams will be assigned to this user"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {teamsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
  )}
              renderTags={(selectedTeams, getTagProps) =>
                selectedTeams.map((team, index) => (
                  <Chip
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2">{team.name}</Typography>
                        <Typography variant="caption" sx={{ ml: 0.5, opacity: 0.7 }}>
                          ({team.school_count || 0})
                        </Typography>
                      </Box>
                    }
                    {...getTagProps({ index })}
                    color="primary"
                    variant="outlined"
                  />
                ))
              }
              sx={{ mb: 4 }}
            />
            
            {/* Selected teams summary */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Selected Teams: {formData.teams.length}
            </Typography>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Button 
                  variant="outlined" 
                  onClick={() => setActiveTab(0)}
                  sx={{ mr: 2 }}
                >
                  Back
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setMode('list');
                    resetForm();
                  }}
                  startIcon={<Cancel />}
                >
                  Cancel
                </Button>
              </Box>
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={<Save />}
                  sx={{ mr: 2 }}
                >
                  {loading ? 'Saving...' : (mode === 'edit' ? 'Save Changes' : 'Create User')}
                </Button>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={() => setActiveTab(2)}
                >
                  Next: Assign Team Groups
                </Button>
              </Box>
            </Box>
          </>
        )}
        
        {/* Tab 3: Assign Team Groups */}
        {activeTab === 2 && (
          <>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Assign Team Groups
            </Typography>
            
            {/* Similar multi-select with Chips for team groups */}
            <Autocomplete
              multiple
              id="team-groups-autocomplete"
              options={availableTeamGroups
                .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, {sensitivity: 'base'}))
              }
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={loading} 
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography>{option.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      ({option.teams ? Object.keys(option.teams).length : 0} teams)
                    </Typography>
                  </Box>
                </li>
              )}
              value={availableTeamGroups.filter(group => 
                formData.teamGroups.includes(group.id)
              )}
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  teamGroups: newValue.map(group => group.id)
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Search and select team groups"
                  placeholder="Type to search team groups"
                  helperText="Selected team groups will be assigned to this user"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(selectedGroups, getTagProps) =>
                selectedGroups.map((group, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2">{group.name}</Typography>
                          <Typography variant="caption" sx={{ ml: 0.5, opacity: 0.7 }}>
                            ({group.teams ? Object.keys(group.teams).length : 0})
                          </Typography>
                        </Box>
                      }
                      {...chipProps}
                      color="primary"
                      variant="outlined"
                    />
                  );
                })
              }
              sx={{ mb: 4 }}
            />
            
            {/* Selected team groups summary */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Selected Team Groups: {formData.teamGroups.length}
            </Typography>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Button
                  variant="outlined"
                  onClick={() => setActiveTab(1)}
                  sx={{ mr: 2 }}
                >
                  Back
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setMode('list');
                    resetForm();
                  }}
                  startIcon={<Cancel />}
                >
                  Cancel
                </Button>
              </Box>
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={<Save />}
                  sx={{ mr: 2 }}
                >
                  {loading ? 'Saving...' : (mode === 'edit' ? 'Save Changes' : 'Create User')}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setActiveTab(3)}
                >
                  Next: City Explorer Locations
                </Button>
              </Box>
            </Box>
          </>
        )}

        {/* Tab 4: City Explorer Locations */}
        {activeTab === 3 && (
          <>
            <Typography variant="h6" sx={{ mb: 1 }}>
              City Explorer Locations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Only applies to users with City Explorer product access. Controls which state/site
              maps this user can see once they open City Explorer.
            </Typography>

            <Autocomplete
              multiple
              id="city-explorer-locations-autocomplete"
              options={Array.isArray(availableCityExplorerLocations) ? availableCityExplorerLocations : []}
              getOptionLabel={(option) => option.name || option.id}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={cityExplorerLocationsLoading}
              value={(Array.isArray(availableCityExplorerLocations) ? availableCityExplorerLocations : []).filter(
                (loc) => formData.cityExplorerLocations.includes(loc.id)
              )}
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  cityExplorerLocations: newValue.map((loc) => loc.id)
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Search and select locations"
                  placeholder="Type to search locations"
                  helperText="Selected locations will be assigned to this user"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {cityExplorerLocationsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(selectedLocations, getTagProps) =>
                selectedLocations.map((loc, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={loc.name || loc.id}
                      {...chipProps}
                      color="primary"
                      variant="outlined"
                    />
                  );
                })
              }
              sx={{ mb: 4 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Selected Locations: {formData.cityExplorerLocations.length}
            </Typography>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Button
                  variant="outlined"
                  onClick={() => setActiveTab(2)}
                  sx={{ mr: 2 }}
                >
                  Back
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setMode('list');
                    resetForm();
                  }}
                  startIcon={<Cancel />}
                >
                  Cancel
                </Button>
              </Box>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={<Save />}
              >
                {loading ? 'Saving...' : (mode === 'edit' ? 'Save Changes' : 'Create User')}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );

  const validateForm = () => {
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      enqueueSnackbar('Please enter a valid email address', { variant: 'error' });
      return false;
    }
  
    // Name validation
    if (!formData.firstName || formData.firstName.trim().length < 1) {
      enqueueSnackbar('Please enter a valid first name', { variant: 'error' });
      return false;
    }

    if (!formData.lastName || formData.lastName.trim().length < 1) {
      enqueueSnackbar('Please enter a valid last name', { variant: 'error' });
      return false;
    }
  
    // Role validation
    if (!formData.role) {
      enqueueSnackbar('Please select a role', { variant: 'error' });
      return false;
    }
  
    // Department validation
    if (!formData.department) {
      enqueueSnackbar('Please select a department', { variant: 'error' });
      return false;
    }
  
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
  
    setLoading(true);
    
    try {
      // Create a copy of formData without teams for the main user update
      const userDataWithoutTeams = { ...formData };
      delete userDataWithoutTeams.teams;
      delete userDataWithoutTeams.teamGroups;
      delete userDataWithoutTeams.cityExplorerLocations;

      if (mode === 'edit') {
        // Update main user data (without teams and teamgroups)
        await adminService.updateUser(selectedUser.uid, userDataWithoutTeams);
        
        // Handle team assignments separately using the dedicated endpoint
        if (formData.teams && formData.teams.length > 0) {
          await adminService.assignUserToTeams(selectedUser.uid, formData.teams, formData.role || 'member');
        } else {
          // If no teams selected, clear existing assignments
          await adminService.assignUserToTeams(selectedUser.uid, []);
        }
        
        // Handle teamgroup assignments separately using the dedicated endpoint
        if (formData.teamGroups && formData.teamGroups.length > 0) {
          await adminService.assignUserToTeamGroups(selectedUser.uid, formData.teamGroups, formData.role || 'member');
        } else {
          // If no teamgroups selected, clear existing assignments
          await adminService.assignUserToTeamGroups(selectedUser.uid, []);
        }

        // Handle City Explorer location assignments separately using the dedicated endpoint
        await adminService.assignUserToCityExplorerLocations(selectedUser.uid, formData.cityExplorerLocations || []);
        
        enqueueSnackbar('User updated successfully', { variant: 'success' });
      } else {
        // For new users, create the user first
        const createdUserResponse = await adminService.createUser(formData);
        
        // Then assign teams if any were selected
        if (formData.teams && formData.teams.length > 0 && createdUserResponse.uid) {
          await adminService.assignUserToTeams(createdUserResponse.uid, formData.teams, formData.role || 'member');
        }
        
        // Then assign teamgroups if any were selected
        if (formData.teamGroups && formData.teamGroups.length > 0 && createdUserResponse.uid) {
          await adminService.assignUserToTeamGroups(createdUserResponse.uid, formData.teamGroups, formData.role || 'member');
        }

        // Then assign City Explorer locations if any were selected
        if (formData.cityExplorerLocations && formData.cityExplorerLocations.length > 0 && createdUserResponse.uid) {
          await adminService.assignUserToCityExplorerLocations(createdUserResponse.uid, formData.cityExplorerLocations);
        }
        
        enqueueSnackbar('User created successfully - you can now send them an invite', { variant: 'success' });
      }
      await fetchUsers();
      setMode('list');
      resetForm();
    } catch (err) {
      if (err.message.includes('already exists')) {
        enqueueSnackbar(err.message, { 
          variant: 'info',
          preventDuplicate: true,
          autoHideDuration: 4000
        });
      } else {
        enqueueSnackbar(
          err.message.includes('Failed to fetch') ? 
            'Could not connect to server. Please try again.' : 
            err.message, 
          { 
            variant: 'error',
            preventDuplicate: true
          }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async (file) => {
    setLoading(true);
    setCsvProcessing(true); // Show CSV processing dialog
    
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true, // This helps skip completely empty rows
      complete: async (results) => {
        try {
          setCsvUploadOpen(false);
          
          // Filter out rows where email is empty or just whitespace
          const filteredData = results.data.filter(row => 
            row.email && row.email.trim() !== ''
          );
          
          const processedData = filteredData.map(row => ({
            email: row.email.trim(), // Trim whitespace from email
            firstName: row.firstName || '',
            lastName: row.lastName || '',
            department: row.department || '',
            role: row.role || '',
            funder: row.funder || '',
            teams: row.teams ? row.teams.split(',').map(t => t.trim()) : [],
            teamGroups: row.teamGroups ? row.teamGroups.split(',').map(g => g.trim()) : []
          }));
          
          const validationResponse = await adminService.bulkCreateUsers({
            users: processedData,
            validate_only: true
          });
          
          setVerificationData(validationResponse);
          setVerificationOpen(true);
        } catch (err) {
          enqueueSnackbar(`Upload failed: ${err.message}`, { variant: 'error' });
        } finally {
          setLoading(false);
          setCsvProcessing(false); // Hide CSV processing dialog
        }
      },
      error: (err) => {
        setLoading(false);
        setCsvProcessing(false); // Hide CSV processing dialog
        enqueueSnackbar(`Failed to parse CSV: ${err.message}`, { variant: 'error' });
      }
    });
  };
  

  const downloadTemplate = () => {
    const headers = ['email', 'firstName', 'lastName', 'department', 'role', 'teams', 'teamGroups', 'funder'];
    const csv = Papa.unparse({
      fields: headers,
      data: []
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      department: '',
      role: '',
      products: ['school_explorer'],
      teams: [],
      teamGroups: [],
      cityExplorerLocations: [],
      funder: ''
    });
    setSelectedUser(null);
  };

  return (
    <Container maxWidth="xl">
      {/* Compact Activity Stats */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
          User Activity Overview
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatsCard 
              title="Total Users" 
              value={totalUsers > 0 ? totalUsers : users.length} 
              icon={Group}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatsCard 
              title="Active Users" 
              value={users.filter(u => {
                if (!u.lastSignInTime) return false;
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                return new Date(u.lastSignInTime) > thirtyDaysAgo;
              }).length}
              icon={CheckCircle}
              color="success.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatsCard 
              title="Inactive Users" 
              value={users.filter(u => {
                if (!u.lastSignInTime) return false;
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                return new Date(u.lastSignInTime) <= thirtyDaysAgo;
              }).length}
              icon={AccessTime}
              color="warning.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatsCard 
              title="Pending Setup" 
              value={users.filter(u => !u.lastSignInTime).length}
              icon={HourglassEmpty}
              color="info.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Tooltip title="Users without any team or team group assignments">
              <Box>
                <StatsCard 
                  title="No Teams" 
                  value={users.filter(u => {
                    const hasTeams = u.teams && u.teams.length > 0;
                    const hasTeamGroups = u.teamGroups && u.teamGroups.length > 0;
                    return !hasTeams && !hasTeamGroups;
                  }).length}
                  icon={Group}
                  color="error.main"
                />
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* Controls Bar - Search, Filters, and Actions */}
      {mode === 'list' && (
        <Box sx={{ 
          mb: 3, 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'space-between'
        }}>
          {/* Search and Filters - Left side */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', flexGrow: 1 }}>
            <TextField
              placeholder="Search users by name, email, role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                minWidth: { xs: '200px', md: '300px' },
                backgroundColor: 'white',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              size="small"
            />

            <FormControl 
              size="small" 
              sx={{ 
                minWidth: '160px',
                backgroundColor: 'white',
              }}
            >
              <Select
                value={funderFilter || ''}
                onChange={(e) => setFunderFilter(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">All Funders</MenuItem>
                <MenuItem value="none">No Funder</MenuItem>
                {Array.from(new Set(users.map(u => u.funder).filter(Boolean))).sort().map(funder => (
                  <MenuItem key={funder} value={funder}>
                    {funder}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl 
              size="small" 
              sx={{ 
                minWidth: '200px',
                backgroundColor: 'white',
              }}
            >
              <Select
                value={teamAssignmentFilter}
                onChange={(e) => setTeamAssignmentFilter(e.target.value)}
                displayEmpty
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="no_teams">No Teams</MenuItem>
                <MenuItem value="no_team_groups">No Team Groups</MenuItem>
                <MenuItem value="no_teams_or_groups">No Teams or Groups</MenuItem>
                <MenuItem value="has_teams">Has Teams/Groups</MenuItem>
              </Select>
            </FormControl>

            <FormControl 
              size="small" 
              sx={{ 
                minWidth: '180px',
                backgroundColor: 'white',
              }}
            >
              <Select
                value={roleFilter || ''}
                onChange={(e) => setRoleFilter(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">All Roles</MenuItem>
                {ROLES.map(role => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl 
                size="small" 
                sx={{ 
                  minWidth: '180px',
                  backgroundColor: 'white',
                }}
              >
                <Select
                  value={lastLoginFilter === 'dateRange' ? 'dateRange' : lastLoginFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'dateRange') {
                      setShowDateRangePicker(true);
                    } else {
                      setLastLoginFilter(value);
                      setLoginDateRange({ start: null, end: null });
                    }
                  }}
                  onOpen={(e) => {
                    // If already on dateRange and user clicks dropdown, reopen picker
                    if (lastLoginFilter === 'dateRange') {
                      e.preventDefault();
                      setShowDateRangePicker(true);
                    }
                  }}
                  displayEmpty
                  renderValue={(value) => {
                    if (value === 'dateRange') {
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <span>{getDateRangeLabel()}</span>
                          <Tooltip title="Edit date range">
                            <IconButton
                              size="small"
                              sx={{ ml: 'auto', p: 0.5 }}
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent dropdown from opening
                                setShowDateRangePicker(true);
                              }}
                            >
                              <DateRangeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    }
                    const options = {
                      all: 'All Activity',
                      never: 'Never Logged In',
                      recent: 'Recent (30 days)',
                      old: 'Inactive (90+ days)'
                    };
                    return options[value] || 'All Activity';
                  }}
                >
                  <MenuItem value="all">All Activity</MenuItem>
                  <MenuItem value="never">Never Logged In</MenuItem>
                  <MenuItem value="recent">Recent (30 days)</MenuItem>
                  <MenuItem value="old">Inactive (90+ days)</MenuItem>
                  <Divider />
                  <MenuItem value="dateRange">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DateRangeIcon fontSize="small" />
                      {lastLoginFilter === 'dateRange' ? 'Change Date Range...' : 'Custom Date Range...'}
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              
              {lastLoginFilter === 'dateRange' && (
                <IconButton 
                  size="small" 
                  onClick={clearDateRange}
                  title="Clear date range filter"
                  sx={{ backgroundColor: 'white' }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            <ToggleButtonGroup
              value={selectedStatus}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) {
                  setSelectedStatus(newValue);
                }
              }}
              size="small"
              sx={{ 
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  px: 2,
                },
              }}
            >
              <ToggleButton value="all">All Users</ToggleButton>
              <ToggleButton value="active">Active</ToggleButton>
              <ToggleButton value="inactive">Inactive</ToggleButton>
              <ToggleButton value="suspended">Suspended</ToggleButton>
              <ToggleButton value="pending">Pending Setup</ToggleButton>
              <ToggleButton value="needs_invite">Needs Invite</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          {/* Action Buttons - Right side */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => {
                clearAdminCache('users');
                setTimeout(() => fetchUsers(), 100);
              }}
              startIcon={<Refresh />}
              size="small"
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              onClick={handleTestEmail}
              disabled={testEmailLoading}
              startIcon={<Email />}
              size="small"
            >
              Test Email
            </Button>
            <Button
              variant="outlined"
              onClick={() => setAnnouncementDialogOpen(true)}
              startIcon={<Campaign />}
              size="small"
            >
              Send Announcement
            </Button>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(e, newMode) => newMode && setMode(newMode)}
            >
              <ToggleButton value="list">
                <Tooltip title="List Users">
                  <Group />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="create">
                <Tooltip title="Add User">
                  <PersonAdd />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="bulk" onClick={() => setCsvUploadOpen(true)}>
                <Tooltip title="Bulk Upload">
                  <Upload />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      )}

      {loading && mode === 'list' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {mode === 'list' && renderUserList()}
          {(mode === 'create' || mode === 'edit') && renderForm()}
        </>
      )}

      {/* Announcement Email Dialog */}
      <AnnouncementEmailDialog
        open={announcementDialogOpen}
        onClose={() => setAnnouncementDialogOpen(false)}
      />

      {/* CSV Upload Dialog */}
      <Dialog open={csvUploadOpen} onClose={() => !csvProcessing && setCsvUploadOpen(false)}>
        <DialogTitle>Bulk Upload Users</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  handleBulkUpload(e.target.files[0]);
                }
              }}
              style={{ display: 'none' }}
              id="csv-upload"
              disabled={csvProcessing}
            />
            <label htmlFor="csv-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<Upload />}
                sx={{ mb: 2 }}
                disabled={csvProcessing}
              >
                Select CSV File
              </Button>
            </label>
            <Typography variant="body2" color="text.secondary">
              or drag and drop your CSV file here
            </Typography>
            <Box sx={{ mt: 3, mb: 2 }}>
              <Button
                onClick={downloadTemplate}
                startIcon={<Download />}
                disabled={csvProcessing}
              >
                Download CSV Template
              </Button>
            </Box>
            {csvProcessing && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Processing file...
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvUploadOpen(false)} disabled={csvProcessing}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deletingUser) {  // Only allow closing if not in the middle of deleting
            setDeleteDialogOpen(false);
            setUserToDelete(null);
          }
        }}
      >
        <DialogTitle>Confirm User Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {userToDelete?.name || userToDelete?.email}?
            This action cannot be undone.
          </Typography>
          {userToDelete?.uid === user?.uid && (
            <Typography color="error" sx={{ mt: 2 }}>
              Warning: You are about to delete your own account. You will be logged out.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setUserToDelete(null);
            }}
            disabled={deletingUser === userToDelete?.uid}
          >
            Cancel
          </Button>
          <Button 
            color="error" 
            onClick={async () => {
              try {
                setDeletingUser(userToDelete.uid);
                await adminService.deleteUser(userToDelete.uid, userToDelete.email);
                
                // Only update UI after successful API call
                await fetchUsers();
                enqueueSnackbar('User deleted successfully', { variant: 'success' });
                
                setDeleteDialogOpen(false);
                setUserToDelete(null);
              } catch (error) {
                enqueueSnackbar(error.message, { variant: 'error' });
              } finally {
                setDeletingUser(null);
              }
            }}
            disabled={deletingUser === userToDelete?.uid}
            startIcon={deletingUser === userToDelete?.uid ? <CircularProgress size={20} /> : null}
          >
            {deletingUser === userToDelete?.uid ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
      <BulkUserUpload
          open={verificationOpen}
          onClose={() => {
            setVerificationOpen(false);
            setVerificationData(null);
          }}
          data={verificationData}
          loading={loading}
          onProceed={async () => {
            try {
              setLoading(true);
              const response = await adminService.bulkCreateUsers({
                users: verificationData.valid_users,
                validate_only: false
              });
              setVerificationOpen(false);
              setMode('list');
              await fetchUsers();
              
              enqueueSnackbar(
                `Successfully created ${response.success_count} users${response.failed_count > 0 ? ` (${response.failed_count} failed)` : ''}`,
                { variant: 'success' }
              );
              
              if (response.failed_count > 0) {
                enqueueSnackbar('Some users failed to create. Check the console for details.', { variant: 'warning' });              }
            } catch (error) {
              enqueueSnackbar(`Upload failed: ${error.message}`, { variant: 'error' });
            } finally {
              setLoading(false);
            }
          }}
        />
      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog />
      <Dialog open={progressDialog.open} maxWidth="sm" fullWidth>
      <DialogTitle>
        {bulkDeleteInProgress ? 'Deleting Users' : 'Sending Invitations'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1" gutterBottom>
            {bulkDeleteInProgress 
              ? `Deleting users (${progressDialog.processed}/${progressDialog.total})` 
              : 'Sending invitations...'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This may take a moment
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
      <CSVProcessingDialog open={csvProcessing} />

      {/* Date Range Picker Dialog */}
      <Dialog 
        open={showDateRangePicker} 
        onClose={() => setShowDateRangePicker(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DateRangeIcon />
            Filter by Login Date Range
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select a date range to filter users by their last login date. Leave either field empty for an open-ended range.
            </Typography>
            
            <Stack spacing={3}>
              <TextField
                label="From Date"
                type="date"
                value={loginDateRange.start || ''}
                onChange={(e) => 
                  setLoginDateRange(prev => ({ 
                    ...prev, 
                    start: e.target.value || null 
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
              
              <TextField
                label="To Date"
                type="date"
                value={loginDateRange.end || ''}
                onChange={(e) => 
                  setLoginDateRange(prev => ({ 
                    ...prev, 
                    end: e.target.value || null 
                  }))
                }
                inputProps={{
                  min: loginDateRange.start || undefined
                }}
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />

            {(loginDateRange.start || loginDateRange.end) && (
              <Box sx={{ 
                backgroundColor: 'grey.50', 
                p: 2, 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Preview:</strong> {getDateRangeLabel()}
                </Typography>
              </Box>
            )}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearDateRange} color="inherit">
            Clear
          </Button>
          <Button onClick={() => setShowDateRangePicker(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={applyDateRange} 
            variant="contained"
            disabled={!loginDateRange.start && !loginDateRange.end}
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default React.memo(UserManagementPanel);